/**
 * CashFlowDetail + CashflowSourceBadge + MonthCard
 * Extracted from DetailPage.jsx (A12 Sprint 7).
 *
 * 12-month cash flow projection with stacked bar chart, simulation,
 * manual event entry, settings panel, and expandable monthly breakdown.
 *
 * Co-extracted private helpers:
 *   - CF_SOURCE_ICONS (constant — icon map for data sources)
 *   - CashflowSourceBadge (inline badge showing data source type)
 *   - MonthCard (expandable monthly breakdown card with inflow/outflow lines)
 *
 * A6-26 fix: raw fetch → apiClient.get (refreshCashflow function)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../../lib/apiClient';
import {
  Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Bar,
  ReferenceLine,
} from 'recharts';

const TOOLTIP_STYLE = {
  background: 'var(--aif-bg-elevated)',
  border: '1px solid var(--aif-border-default)',
  borderRadius: 12,
  fontSize: 12,
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--aif-text-muted)' };

// ─── CF_SOURCE_ICONS ─────────────────────────────────────────
const CF_SOURCE_ICONS = {
  contract: '📋',
  harvest_real: '✏️',
  harvest_estimate: '📊',
  subsidy_tracked: '✅',
  subsidy_estimate: '📊',
  milk_real: '🥛',
  manual: '👤',
  reference: '📊',
  overhead: '📊',
};

// ─── CashflowSourceBadge ────────────────────────────────────
function CashflowSourceBadge({ dataSource }) {
  const icon = CF_SOURCE_ICONS[dataSource] || '📊';
  const isReal = ['contract', 'harvest_real', 'subsidy_tracked', 'milk_real', 'manual'].includes(dataSource);
  return (
    <span className={`inline-flex items-center text-[9px] px-1 py-0.5 rounded border ${
      isReal ? 'bg-[var(--aif-color-primary)]/10 border-[var(--aif-color-primary)]/30 text-[var(--aif-color-primary)]'
              : 'bg-[var(--aif-bg-elevated)] border-[var(--aif-border-default)] text-[var(--aif-text-muted)]'
    }`} title={dataSource}>
      {icon}
    </span>
  );
}

// ─── MonthCard ───────────────────────────────────────────────
function MonthCard({ month, currency, t }) {
  const [expanded, setExpanded] = useState(false);
  const isGap = month.status === 'gap';
  const isWarning = month.status === 'warning';
  const realPct = month.real_data_pct || 0;

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isGap ? 'border-red-500/40 bg-red-500/5' :
      isWarning ? 'border-yellow-500/30 bg-yellow-500/5' :
      'border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)]'
    }`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full p-3 text-left">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isGap && <span className="w-2 h-2 rounded-full bg-red-500" />}
            {isWarning && <span className="w-2 h-2 rounded-full bg-yellow-500" />}
            {!isGap && !isWarning && <span className="w-2 h-2 rounded-full bg-green-500" />}
            <span className="text-sm font-medium text-[var(--aif-text-primary)]">{month.month_label}</span>
            {realPct > 0 && (
              <span className="text-[9px] text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)]/30 px-1 rounded">{realPct}% real</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-bold ${month.net_flow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {month.net_flow >= 0 ? '+' : ''}{month.net_flow.toLocaleString()} {currency}
            </span>
            <svg className={`w-3.5 h-3.5 text-[var(--aif-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-[var(--aif-text-muted)]">
          <span className="text-green-400">+{month.total_inflow.toLocaleString()}</span>
          <span className="text-red-400">-{month.total_outflow.toLocaleString()}</span>
          <span className="ml-auto">{t('cashflow.balance')}: {month.cumulative_balance.toLocaleString()} {currency}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-[var(--aif-border-default)] pt-2 space-y-2">
          {month.inflows.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-green-400 uppercase mb-1">{t('cashflow.inflows')}</div>
              {month.inflows.map((item, j) => (
                <div key={j} className="flex justify-between items-center text-xs py-0.5">
                  <span className="text-[var(--aif-text-muted)] flex items-center gap-1">
                    {item.source}
                    {item.data_source && <CashflowSourceBadge dataSource={item.data_source} />}
                  </span>
                  <span className="text-green-400 flex-shrink-0 ml-2">+{item.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
          {month.outflows.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-red-400 uppercase mb-1">{t('cashflow.outflows')}</div>
              {month.outflows.map((item, j) => (
                <div key={j} className="flex justify-between items-center text-xs py-0.5">
                  <span className="text-[var(--aif-text-muted)] flex items-center gap-1">
                    {item.source}
                    {item.data_source && <CashflowSourceBadge dataSource={item.data_source} />}
                  </span>
                  <span className="text-red-400 flex-shrink-0 ml-2">-{item.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CashFlowDetail (main) ──────────────────────────────────
export default function CashFlowDetail({ cashflow, token }) {
  const { t } = useTranslation();
  const [simData, setSimData] = useState(null);
  const [simPriceChg, setSimPriceChg] = useState('');
  const [showSim, setShowSim] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [eventForm, setEventForm] = useState({ date: '', description: '', amount: '', category: 'OTHER', recurring: 'NONE' });
  const [settingsForm, setSettingsForm] = useState({ starting_balance: '', monthly_overhead_estimate: '', monthly_living_costs: '', tax_prepayment_quarterly: '' });
  const [localData, setLocalData] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');

  const data = localData || simData || cashflow;

  const refreshCashflow = async () => {
    if (!token) return;
    try {
      const d = await apiClient.get('/api/v1/aif/finance/cashflow');
      setLocalData(d);
    } catch { /* ignore */ }
  };

  const handleAddEvent = async () => {
    if (!token || !eventForm.date || !eventForm.amount) return;
    try {
      await apiClient.post('/api/v1/aif/finance/cashflow/events', { ...eventForm, amount: parseFloat(eventForm.amount) }, {
        entityType: 'cashflow_event', queueOnOffline: true,
      });
      setShowEventForm(false);
      setEventForm({ date: '', description: '', amount: '', category: 'OTHER', recurring: 'NONE' });
      await refreshCashflow();
      setSavedMsg(t('finance.overridesSaved'));
      setTimeout(() => setSavedMsg(''), 1500);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  const handleSaveSettings = async () => {
    if (!token) return;
    const payload = {};
    if (settingsForm.starting_balance) payload.starting_balance = parseFloat(settingsForm.starting_balance);
    if (settingsForm.monthly_overhead_estimate) payload.monthly_overhead_estimate = parseFloat(settingsForm.monthly_overhead_estimate);
    if (settingsForm.monthly_living_costs) payload.monthly_living_costs = parseFloat(settingsForm.monthly_living_costs);
    if (settingsForm.tax_prepayment_quarterly) payload.tax_prepayment_quarterly = parseFloat(settingsForm.tax_prepayment_quarterly);
    try {
      await apiClient.patch('/api/v1/aif/finance/cashflow/settings', payload, {
        entityType: 'cashflow_settings', queueOnOffline: true,
      });
      setShowSettings(false);
      await refreshCashflow();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  if (!data?.projection?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-4xl mb-4">💧</div>
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">{t('cashflow.title')}</h3>
        <p className="text-xs text-[var(--aif-text-muted)] max-w-[250px]">{t('cashflow.noData')}</p>
      </div>
    );
  }

  const currency = data.currency || 'EUR';
  const chartData = data.projection.map(m => ({
    name: m.month_label?.slice(5) || `${m.month}`,
    inflow: m.total_inflow,
    outflow: -m.total_outflow,
    balance: m.cumulative_balance,
    status: m.status,
  }));

  const runSimulation = async () => {
    if (!token || !simPriceChg) return;
    try {
      try {
        const r = await apiClient.post('/api/v1/aif/finance/cashflow/simulate', { price_change_pct: parseFloat(simPriceChg) }, {
          entityType: 'cashflow_simulation', queueOnOffline: false,
        });
        if (r?.data) setSimData(r.data);
      } catch (err) {
        if (!(err instanceof ApiError)) throw err;
      }
    } catch { /* ignore */ }
  };

  const resetSim = () => { setSimData(null); setSimPriceChg(''); };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="text-center py-3">
        <div className="text-xs text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">{t('cashflow.forecast12')}</div>
        <div className="flex justify-center gap-8">
          <div>
            <div className="text-xl font-bold text-green-400">
              +{(data.total_inflow_year || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.totalInflow')}</div>
          </div>
          <div>
            <div className="text-xl font-bold text-red-400">
              -{(data.total_outflow_year || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.totalOutflow')}</div>
          </div>
        </div>
        {data.has_gaps && (
          <div className="mt-3 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-400 font-medium">
            {t('cashflow.gapWarning', {
              month: data.first_gap_month,
              amount: (data.first_gap_shortfall || 0).toLocaleString(),
              currency,
            })}
          </div>
        )}
        {simData && (
          <div className="mt-2 text-xs text-yellow-400 font-medium">{t('cashflow.simActive')}</div>
        )}
      </div>

      {/* Stacked bar chart: inflows (green up), outflows (red down), balance line */}
      <div>
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-3 uppercase tracking-wide">
          {t('cashflow.monthlyFlow')}
        </h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => `${Math.abs(v).toLocaleString()} ${currency}`} />
              <ReferenceLine y={0} stroke="var(--aif-text-muted)" strokeWidth={1} />
              <Bar dataKey="inflow" fill="#4ade80" opacity={0.7} name={t('cashflow.inflows')} />
              <Bar dataKey="outflow" fill="#f87171" opacity={0.7} name={t('cashflow.outflows')} />
              <Line type="monotone" dataKey="balance" stroke="var(--aif-color-primary)" strokeWidth={2} dot={{ r: 3 }} name={t('cashflow.balance')} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Simulation toggle */}
      <div>
        <button
          onClick={() => setShowSim(!showSim)}
          className="w-full py-2 rounded-xl text-xs font-medium border border-[var(--aif-border-default)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] transition"
        >
          {showSim ? t('cashflow.hideSim') : t('cashflow.showSim')}
        </button>
        {showSim && (
          <div className="mt-3 p-4 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] space-y-3">
            <label className="text-xs text-[var(--aif-text-secondary)]">{t('cashflow.priceChange')}</label>
            <div className="flex gap-2">
              {[-20, -10, 10, 20].map(v => (
                <button
                  key={v}
                  onClick={() => setSimPriceChg(String(v))}
                  className={`flex-1 py-1.5 rounded-lg text-xs border transition ${
                    simPriceChg === String(v)
                      ? 'border-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)]'
                      : 'border-[var(--aif-border-default)] text-[var(--aif-text-muted)]'
                  }`}
                >
                  {v > 0 ? '+' : ''}{v}%
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={runSimulation}
                disabled={!simPriceChg}
                className="flex-1 py-2 rounded-xl text-xs font-medium bg-[var(--aif-color-primary)] text-white disabled:opacity-40 transition"
              >
                {t('cashflow.runSim')}
              </button>
              {simData && (
                <button onClick={resetSim} className="px-4 py-2 rounded-xl text-xs border border-[var(--aif-border-default)] text-[var(--aif-text-muted)]">
                  {t('cashflow.resetSim')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Real data source bar */}
      {data.overall_real_data_pct > 0 ? (
        <div className="rounded-xl bg-[var(--aif-color-primary)]/10 border border-[var(--aif-color-primary)]/30 px-3 py-2 text-xs text-[var(--aif-color-primary)] flex items-center gap-2">
          <span>✏️</span>
          <span>{t('cashflow.realDataPct', { pct: data.overall_real_data_pct })}</span>
        </div>
      ) : (
        <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] px-3 py-2 text-xs text-[var(--aif-text-muted)] flex items-center gap-2">
          <span>📊</span>
          <span>{t('cashflow.referenceBasedPlan')}</span>
        </div>
      )}

      {/* Enhanced gap warning with breakdown */}
      {data.has_gaps && data.first_gap_top_expenses && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-xs">
          <div className="text-red-400 font-semibold mb-1">
            {t('cashflow.gapDetail', { month: data.first_gap_month, amount: (data.first_gap_shortfall || 0).toLocaleString(), currency })}
          </div>
          <div className="text-[var(--aif-text-muted)] text-[11px]">{t('cashflow.topExpenses')}</div>
          {data.first_gap_top_expenses.slice(0,2).map((exp, i) => (
            <div key={i} className="text-[11px] text-red-300">• {exp.source}: {exp.amount.toLocaleString()} {currency}</div>
          ))}
        </div>
      )}

      {/* Add manual event + settings buttons */}
      <div className="flex gap-2">
        <button onClick={() => setShowEventForm(s => !s)}
          className="flex-1 py-2 rounded-xl text-xs font-medium border border-dashed border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)]">
          + {t('cashflow.eventAdd')}
        </button>
        <button onClick={() => setShowSettings(s => !s)}
          className="px-3 py-2 rounded-xl text-xs border border-[var(--aif-border-default)] text-[var(--aif-text-muted)]">
          ⚙️
        </button>
      </div>
      {savedMsg && <div className="text-xs text-green-400 text-center">{savedMsg}</div>}

      {/* Manual event form */}
      {showEventForm && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-2.5">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('cashflow.eventAdd')}</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.eventDate')}</label>
              <input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.eventAmount')}</label>
              <input type="number" step="0.01" placeholder={t('cashflow.eventAmountPlaceholder')}
                value={eventForm.amount} onChange={e => setEventForm({...eventForm, amount: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.eventDesc')}</label>
            <input type="text" placeholder={t('cashflow.eventDescPlaceholder')}
              value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})}
              className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.eventCategory')}</label>
              <select value={eventForm.category} onChange={e => setEventForm({...eventForm, category: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
                {['HARVEST','SUBSIDY','CONTRACT','INPUT','MACHINERY','LABOR','TAX','INSURANCE','OTHER'].map(c => (
                  <option key={c} value={c}>{t(`cashflow.cat${c.charAt(0)+c.slice(1).toLowerCase()}`) || c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('cashflow.eventRecurring')}</label>
              <select value={eventForm.recurring} onChange={e => setEventForm({...eventForm, recurring: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
                <option value="NONE">{t('cashflow.eventNone')}</option>
                <option value="MONTHLY">{t('cashflow.eventMonthly')}</option>
                <option value="QUARTERLY">{t('cashflow.eventQuarterly')}</option>
                <option value="ANNUALLY">{t('cashflow.eventAnnually')}</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddEvent}
              className="flex-1 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-xs font-medium">
              {t('cashflow.eventSave')}
            </button>
            <button onClick={() => setShowEventForm(false)}
              className="px-4 py-2 rounded-xl border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-muted)]">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Settings panel */}
      {showSettings && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-2.5">
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('cashflow.settingsTitle')}</h3>
          {[
            ['starting_balance', t('cashflow.settingsBalance'), data.starting_balance],
            ['monthly_overhead_estimate', t('cashflow.settingsOverhead'), data.monthly_overhead_estimate],
            ['monthly_living_costs', t('cashflow.settingsLiving'), ''],
            ['tax_prepayment_quarterly', t('cashflow.settingsTax'), ''],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{label}</label>
              <input type="number" step="0.01" placeholder={placeholder ? `${t('cashflow.settingsCurrent')}: ${placeholder}` : currency}
                value={settingsForm[key]} onChange={e => setSettingsForm({...settingsForm, [key]: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
          ))}
          <button onClick={handleSaveSettings}
            className="w-full py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-xs font-medium">
            {t('cashflow.settingsSave')}
          </button>
        </div>
      )}

      {/* Monthly breakdown cards */}
      <div className="space-y-2">
        {data.projection.map((m, i) => (
          <MonthCard key={i} month={m} currency={currency} t={t} />
        ))}
      </div>
    </div>
  );
}
